import React, { useState, useEffect } from 'react';
import Facturar from 'views/Facturacion/FacturaModal';
import TransaccionDetails from './DetallePagos';
import {
  Box,
  Button,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Tabs,
  TabPanels,
  TabPanel,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Badge,
  Flex,
} from '@chakra-ui/react';
import axios from 'axios';
import { ChevronDownIcon } from '@chakra-ui/icons';

interface Pago {
  id: string;
  fechaPago: string;
  monto: number;
  tipoPago: string;
  estado: string;
  UUID: string;
  estatus_de_cancelacion:string;
  RFCReceptor: string;
}

interface HistorialPagosProps {
  idClaveCatastral: string;
  idPredioSys: string;
  data: any;
}

const HistorialPagos: React.FC<HistorialPagosProps> = ({
  idClaveCatastral,
  idPredioSys,
  data,
}) => {
  const [pagosPredio, setPagosPredio] = useState<Pago[]>([]);
  const [selectedPagoId, setSelectedPagoId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false); // Estado para bloquear pantalla

  const toast = useToast();

  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();

  useEffect(() => {
    const fetchPagosPredio = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/${idClaveCatastral}`,
        );
        const data = response.data;

        const formattedPagos = (data.transacciones || []).map((item: any) => ({
          id: item.ID.toString(),
          fechaPago: item.Fecha
            ? new Date(item.Fecha).toISOString()
            : 'Sin Fecha',
          monto: item.Monto,
          tipoPago: item.Concepto,
          UUID: item.UUID,
          estado: item.Fecha ? 'Pagado' : 'Pagado',
          estatus_de_cancelacion:item.estatus_de_cancelacion
        }));

        setPagosPredio(formattedPagos);
      } catch (error) {
        console.error('Error al obtener los pagos de predio:', error);
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los pagos de predio.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPagosPredio();
  }, [idClaveCatastral, toast]);

  const handleVerDetalle = (pagoId: string) => {
    setSelectedPagoId(pagoId);
    onDetailOpen();
  };

  return (
    <Box
      p={6}
      bg="white"
      borderRadius="lg"
      boxShadow="2xl"
      maxW="1400px"
      mx="auto"
      mt={6}
    >
      {loading && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.5)"
          display="flex"
          justifyContent="center"
          alignItems="center"
          zIndex="9999"
        >
          <Spinner size="xl" color="white" />
        </Box>
      )}

      <Box
        bgGradient="linear(to-r, #681a2d, #5b182b)"
        p={6}
        borderTopRadius="lg"
        mb={6}
        boxShadow="lg"
      >
        <Heading as="h1" size="lg" color="white" textAlign="center" fontWeight="bold">
          Historial de Pagos de Predio
        </Heading>
      </Box>

      <Tabs isFitted variant="enclosed">
        <TabPanels>
          <TabPanel>
            {isLoading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="xl" color="#681a2d" thickness="4px" />
                <Text ml={4} fontSize="lg" color="gray.600">
                  Cargando pagos de predio...
                </Text>
              </Flex>
            ) : (
              <Box overflowX="auto" maxHeight="500px" overflowY="auto" position="relative">
                <Table variant="simple" size="sm">
                  <Thead
                    position="sticky"
                    top={0}
                    bgGradient="linear(to-r, #681a2d, #5b182b)"
                    zIndex={1}
                  >
                    <Tr>
                      <Th color="white" fontSize="xs" textTransform="uppercase">ID</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Fecha de Pago</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Monto</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Tipo de Pago</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Estado</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">UUID</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Cancelado</Th>
                      <Th color="white" fontSize="xs" textTransform="uppercase">Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pagosPredio.length > 0 ? (
                      pagosPredio.map((pago) => (
                        <Tr
                          key={pago.id}
                          bgColor={pago.estatus_de_cancelacion === 'CANCELADO' ? 'red.100' : 'transparent'}
                          fontSize="sm"
                          _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                          transition="background 0.2s"
                        >
                          <Td fontWeight="semibold" color="gray.700">{pago.id}</Td>
                          <Td color="gray.600">
                            {pago.fechaPago === 'Sin Fecha'
                              ? pago.fechaPago
                              : new Date(pago.fechaPago).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                          </Td>
                          <Td fontWeight="bold" color="green.600">
                            $
                            {new Intl.NumberFormat('es-MX', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(pago.monto)}
                          </Td>
                          <Td>
                            <Badge colorScheme="blue" variant="solid" fontSize="xs">
                              {pago.tipoPago}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={pago.estado === 'Pendiente' ? 'orange' : 'green'}
                              variant="solid"
                              fontSize="xs"
                            >
                              {pago.estado}
                            </Badge>
                          </Td>
                          <Td>
                            {pago.UUID ? (
                              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                Timbrado
                              </Badge>
                            ) : (
                              <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                Sin timbrar
                              </Badge>
                            )}
                          </Td>
                          <Td>
                            {pago.estatus_de_cancelacion === 'CANCELADO' ? (
                              <Badge colorScheme="red" variant="solid" fontSize="xs">
                                CANCELADO
                              </Badge>
                            ) : (
                              <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                                Activo
                              </Badge>
                            )}
                          </Td>

                          <Td>
                            <Menu>
                              <MenuButton
                                as={Button}
                                size="sm"
                                bgGradient="linear(to-r, #681a2d, #5b182b)"
                                color="white"
                                _hover={{ bgGradient: "linear(to-r, #7a1f36, #6c1e33)" }}
                                _active={{ bgGradient: "linear(to-r, #5a1625, #4d1523)" }}
                                rightIcon={<ChevronDownIcon />}
                              >
                                Opciones
                              </MenuButton>
                              <MenuList zIndex={9999}>
                                {pago.UUID === null ? (
                                  <MenuItem
                                    onClick={async () => {
                                      setLoading(true);
                                      try {
                                        // Paso 1: Timbrar
                                        const response = await axios.get(
                                          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Facturacion/generarx-prod/${pago.id}`,
                                        );

                                        const contentType = response.headers['content-type'];

                                        if (contentType?.includes('application/xml')) {
                                          // Éxito - XML retornado
                                          toast({
                                            title: 'Éxito',
                                            description: 'Factura timbrada exitosamente. Descargando PDF...',
                                            status: 'success',
                                            duration: 3000,
                                            isClosable: true,
                                          });

                                          // Paso 2: Obtener transacción actualizada
                                          const updatedResponse = await axios.get(
                                            `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/detalles/${pago.id}`,
                                          );

                                          // Actualizar el pago en la lista local
                                          setPagosPredio((prevPagos) =>
                                            prevPagos.map((p) =>
                                              p.id === pago.id
                                                ? { ...p, UUID: updatedResponse.data.UUID || p.UUID }
                                                : p
                                            )
                                          );

                                          // Paso 3: Descargar PDF automáticamente
                                          const pdfResponse = await axios.get(
                                            `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/Imprimir/${pago.id}`,
                                            { responseType: 'blob' }
                                          );

                                          const pdfBlob = new Blob([pdfResponse.data], {
                                            type: 'application/pdf',
                                          });
                                          const pdfUrl = URL.createObjectURL(pdfBlob);
                                          const link = document.createElement('a');
                                          link.href = pdfUrl;
                                          link.download = `Factura_${pago.id}.pdf`;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          URL.revokeObjectURL(pdfUrl);
                                        } else {
                                          // Error del PAC
                                          const errorData = response.data;
                                          toast({
                                            title: 'Error al timbrar',
                                            description: errorData.MensajeError || 'Error desconocido',
                                            status: 'error',
                                            duration: 5000,
                                            isClosable: true,
                                          });
                                        }
                                      } catch (error) {
                                        console.error('Error al generar el CFDI:', error);
                                        toast({
                                          title: 'Error',
                                          description: 'No se pudo generar el CFDI. Intenta de nuevo.',
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                      } finally {
                                        setLoading(false);
                                      }
                                    }}
                                  >
                                    Facturar
                                  </MenuItem>
                                ) : (
                                  <MenuItem
                                    onClick={async () => {
                                      setLoading(true); // Activa el bloqueo de pantalla

                                      try {
                                        const response = await axios.get(
                                          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/Imprimir/` +
                                            pago.id,
                                          {
                                            responseType: 'blob',
                                          },
                                        );

                                        const pdfBlob = new Blob(
                                          [response.data],
                                          {
                                            type: 'application/pdf',
                                          },
                                        );
                                        const pdfUrl =
                                          URL.createObjectURL(pdfBlob);
                                          window.open(pdfUrl, '_blank');

                                        // const link =
                                        //   document.createElement('a');
                                        // link.href = pdfUrl;
                                        // link.setAttribute(
                                        //   'download',
                                        //   `CFDI_${pago.UUID}.pdf`,
                                        // );
                                        // document.body.appendChild(link);
                                        // link.click();
                                        // document.body.removeChild(link);

                                        toast({
                                          title: 'Éxito',
                                          description:
                                            'CFDI descargado correctamente.',
                                          status: 'success',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                      } catch (error) {
                                        console.error(
                                          'Error al descargar el CFDI:',
                                          error,
                                        );
                                        toast({
                                          title: 'Error',
                                          description:
                                            'No se pudo descargar el CFDI. Intenta de nuevo.',
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                      } finally {
                                        setLoading(false)
                                      }
                                    }}
                                  >
                                    Imprimir CFDI
                                  </MenuItem>
                                )}
                                <MenuItem
                                  onClick={() => handleVerDetalle(pago.id)}
                                >
                                  Ver Detalle
                                </MenuItem>
                                <MenuItem
  onClick={() => {
    const downloadUrl = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Facturacion/descargar/${pago.UUID}`;
    window.open(downloadUrl, '_blank');
  }}
>
  Descargar XML
</MenuItem>

                                <MenuItem
                                  onClick={async () => {
                                    let toastId; // Identificador para el toast
                                    try {
                                      // Mostrar un toast indicando que se está procesando la cancelación
                                      toastId = toast({
                                        title: 'Procesando',
                                        description:
                                          'La cancelación del CFDI está en curso, espera un momento.',
                                        status: 'info',
                                        duration: null, // El toast permanece visible hasta que lo cierres manualmente
                                        isClosable: true,
                                      });

                                      // Datos requeridos por el backend
                                      const requestData = {
                                        user: 'usuario_desde_bd',
                                        pass: 'password_desde_bd',
                                        rfcEmisor: 'RFC_desde_bd',
                                        listaCFDI: [
                                          {
                                            EsCancelable: true, // Supón que el CFDI es cancelable
                                            FolioSustitucion: '', // Si aplica, puedes incluirlo
                                            Motivo: '03', // Motivo de cancelación: "No se llevó a cabo la operación"
                                            RFCReceptor:
                                              pago.RFCReceptor ||
                                              'XAXX010101000',
                                            Total: pago.monto,
                                            UUID: pago.UUID, // UUID del CFDI que se desea cancelar
                                          },
                                        ],
                                      };

                                      // Realizar la solicitud POST al backend
                                      const response = await axios.post(
                                        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Facturacion/cancelar-cfdi`,
                                        requestData,
                                        { responseType: 'blob' }, // Asegúrate de recibir el archivo como blob
                                      );

                                      if (response.data) {
                                        // Crear un blob a partir del XML
                                        const xmlBlob = new Blob(
                                          [response.data],
                                          { type: 'application/xml' },
                                        );

                                        // Crear un enlace para descargar el archivo
                                        const xmlUrl =
                                          URL.createObjectURL(xmlBlob);
                                        const link =
                                          document.createElement('a');
                                        link.href = xmlUrl;
                                        link.setAttribute(
                                          'download',
                                          `Cancelacion_CFDI_${pago.UUID}.xml`,
                                        );

                                        // Simular clic en el enlace para iniciar la descarga
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);

                                        // Cerrar el toast de progreso y mostrar uno de éxito
                                        toast.close(toastId);
                                        toast({
                                          title: 'Éxito',
                                          description:
                                            'El CFDI fue cancelado y el XML se descargó correctamente.',
                                          status: 'success',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                      } else {
                                        // Cerrar el toast de progreso y mostrar uno de error
                                        toast.close(toastId);
                                        toast({
                                          title: 'Error',
                                          description:
                                            'No se pudo cancelar el CFDI. Intenta de nuevo.',
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                      }
                                    } catch (error) {
                                      console.error(
                                        'Error al cancelar el CFDI:',
                                        error,
                                      );

                                      // Cerrar el toast de progreso y mostrar uno de error
                                      toast.close(toastId);
                                      toast({
                                        title: 'Error',
                                        description:
                                          'No se pudo cancelar el CFDI. Intenta de nuevo.',
                                        status: 'error',
                                        duration: 5000,
                                        isClosable: true,
                                      });
                                    }
                                  }}
                                >
                                  Cancelar CFDI
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={6} textAlign="center">
                          No hay pagos registrados.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal para Ver Detalle */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            {selectedPagoId && (
              <TransaccionDetails transaccionId={selectedPagoId} />
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onDetailClose}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HistorialPagos;
